<template>
  <header
    ref="header"
    class="app-header"
  >
    <div class="app-header__flex app-header__content">
      <div class="app-header__flex">
        <PrmButton
          v-if="hasSidebar"
          v-tooltip="sidebarShortcutHint"
          size="small"
          text
          severity="secondary"
          type="button"
          icon="pi pi-bars"
          style="color: var(--text-color);"
          data-qa="app-header__sidebar-toggle"
          @click="isVisible = true"
        />
        <div
          v-else
          style="width: .5rem;"
        >
          &nbsp;
        </div>
        <slot name="logo" />
      </div>
      <div>
        <slot name="title" />
      </div>
      <div class="flex-row-union">
        <slot name="actions" />
      </div>
    </div>
    <AppSidebar
      v-if="!!$slots.sidebar && hasSlotContent($slots.sidebar)"
      v-model:visible="isVisible"
      @close="close"
    >
      <slot
        name="sidebar"
        :close="close"
        :is-visible="isVisible"
      />
    </AppSidebar>
  </header>
</template>

<script lang="ts" setup>
import PrmButton from '@NitrexGroup/primevue/button';
import {
  rafCallback,
} from '@qmulus/frontend-shared/util/raf';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
} from 'vue';

import {
  EKeyboardKey,
} from '../../constants/keyboard.ts';
import {
  getKeyboardKeySymbol,
  getPlatformCommandKey,
} from '../../utils/keyboard-key-symbol.ts';
import {
  hasSlotContent,
} from '../../utils/vue.ts';

import AppSidebar from './app-sidebar.vue';

const slots = defineSlots<{
  logo(): any,
  title(): any,
  actions(): any,
  sidebar?(props: {
    isVisible: boolean,
    close: () => void,
  }): any,
}>();

const isVisible = shallowRef(false);

const hasSidebar = computed(() => !!slots.sidebar && hasSlotContent(slots.sidebar));

const platformCommandKey = getPlatformCommandKey();

const sidebarShortcutHint = `${getKeyboardKeySymbol(platformCommandKey)}+${getKeyboardKeySymbol(EKeyboardKey.UpperK)}`;

function onSidebarShortcut(event: KeyboardEvent) {
  if (!hasSidebar.value) {
    return;
  }
  const isCommandModifierPressed = platformCommandKey === EKeyboardKey.Meta
    ? event.metaKey
    : event.ctrlKey;
  if (!isCommandModifierPressed || event.key.toLowerCase() !== EKeyboardKey.LowerK) {
    return;
  }
  event.preventDefault();
  isVisible.value = true;
}

const header = ref<HTMLElement | null>(null);

const onResize = rafCallback(() => {
  if (!header.value) {
    return;
  }
  document.documentElement.style.setProperty('--navbar-header-height', `${header.value!.offsetHeight}px`);
});
onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);
  header.value!.addEventListener('resize', onResize);
  window.addEventListener('keydown', onSidebarShortcut);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  header.value!.removeEventListener('resize', onResize);
  window.removeEventListener('keydown', onSidebarShortcut);
});

function close() {
  isVisible.value = false;
}
</script>

<style lang="scss" scoped>
@use '../../styles/variables/z-index';

.app-header {
  background-color: var(--surface-frame);
  z-index: z-index.$app-header;

  @media print {
    display: none;
  }

  &__flex {
    display: flex;
    gap: var(--gap-union);
    align-items: center;
  }

  &__content {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
